import type { ParagraphAncestor, ParagraphEntry, ParagraphIndex } from './indexParagraphs';
import type { CastTableCell } from '../types';

/**
 * Returns the chain of headings that scope a matched paragraph. A heading is a
 * preceding paragraph with a *larger* primary font size than the match — there
 * is no explicit heading level in the Cast, so "bigger text" is the signal
 * (mirrors extract-paragraphs/expand/findHeadingsForParagraph, the v1 design).
 *
 * Walking backwards in document order, each accepted heading raises the bar so
 * we don't grab a smaller heading after a larger one (returns innermost-first,
 * e.g. [H2, H1]). The search:
 * - is scoped to the same section, and to the match's table-cell path or any
 *   ancestor cell (headings can sit above a table, never inside a sibling cell);
 * - skips list items, which carry their own hierarchy; and
 * - when the match is itself in a list, starts above the list, since headings
 *   precede the list rather than sitting among its items.
 *
 * Returns nothing when the match has no font size to compare against.
 */
export function expandHeadings(entry: ParagraphEntry, index: ParagraphIndex): ParagraphEntry[] {
  const baseFontSize = entry.paragraph.primaryStyle?.fontSize;
  if (baseFontSize === undefined) return [];

  let minFontSize = baseFontSize;
  const headings: ParagraphEntry[] = [];
  const startIndex = isListItem(entry) ? findListStartIndex(entry, index) : entry.documentOrder - 1;

  for (let i = startIndex; i >= 0; i--) {
    const candidate = index.ordered[i];
    if (candidate.section !== entry.section) break;
    if (!isHeadingScope(candidate, entry)) continue;
    if (isListItem(candidate)) continue;

    const fontSize = candidate.paragraph.primaryStyle?.fontSize;
    if (fontSize !== undefined && fontSize > minFontSize) {
      headings.push(candidate);
      minFontSize = fontSize;
    }
  }

  return headings;
}

/**
 * Walks back to the first paragraph that begins the match's list — the first
 * non-list paragraph, or the point where we leave the match's scope. Heading
 * search starts there (that paragraph may itself be a heading).
 */
function findListStartIndex(entry: ParagraphEntry, index: ParagraphIndex): number {
  for (let i = entry.documentOrder - 1; i >= 0; i--) {
    const candidate = index.ordered[i];
    if (candidate.section !== entry.section) return i;
    if (!isHeadingScope(candidate, entry)) return i;
    if (!isListItem(candidate)) return i;
  }
  return -1;
}

function isListItem(entry: ParagraphEntry): boolean {
  return (entry.paragraph.listLevel ?? 0) > 0;
}

/**
 * A candidate is a valid heading scope when its table-cell path is a prefix of
 * (or equal to) the match's — i.e. the same cell, an ancestor cell, or outside
 * any table. Sibling or deeper cells are excluded.
 */
function isHeadingScope(candidate: ParagraphEntry, entry: ParagraphEntry): boolean {
  const candidateCells = cellPath(candidate);
  const entryCells = cellPath(entry);
  if (candidateCells.length > entryCells.length) return false;
  return candidateCells.every((cell, i) => cell === entryCells[i]);
}

function cellPath(entry: ParagraphEntry): CastTableCell[] {
  return entry.ancestors.filter(isTableCell);
}

function isTableCell(ancestor: ParagraphAncestor): ancestor is CastTableCell {
  return ancestor.type === 'tableCell';
}
